import { useEffect, useState, useCallback, useMemo } from 'react';
import { captureMessage } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { useAuth, useTenantsState, useTenantsActions } from '@frontegg/react';
import type { ITenantsResponseV2 } from '@frontegg/rest-api';
import { atom, useAtomValue, useStore } from 'jotai';
import { useToastMessageState } from '../../components/ToastMessage/ToastMessageProvider';
import { decodeJwtPayload } from '../../util/jwt';
import { navigateTo } from '../../util/navigation';
import { clearBootCache } from '../boot-cache/bootCache';
import { getActiveTenantParents, getActiveTenantSubTenants } from '../queries/FronteggHierarchyQueries';
import { customerTenantIdState } from '../state/AuthState';
import { getCurrentSubdomain, parseMetadata, tenantListOrEmpty } from './tenantSubdomain';

export interface TenantWithSubdomain extends ITenantsResponseV2 {
  subdomain?: string;
}

interface UseAccountSwitcherReturn {
  tenants: TenantWithSubdomain[];
  /** The account this session is on: the membership whose subdomain the host
   *  carries, which differs from the active account when a parent account is
   *  active. The active account stands in while no membership matches. */
  currentTenant?: ITenantsResponseV2;
  isLoading: boolean;
  isSwitching: boolean;
  error?: string;
  hasMultipleTenants: boolean;
  isInitialTenantLoad: boolean;
  /** True until the on-load check of the token's account ends or runs out of time; the app waits for it. */
  isCheckingAccount: boolean;
  handleSwitchTenant: (tenantId: string) => void;
}

const ACCOUNT_SWITCHED_PARAM = 'account_switched';
// The account the switch left, so the reload never hops straight back to it
const ACCOUNT_SWITCHED_FROM_PARAM = 'account_switched_from';
// Parentage changes rarely; an hour keeps every hook instance on one lookup per session
const HIERARCHY_STALE_TIME_MS = 60 * 60 * 1000;
// Respondents see it too, so it says nothing about accounts
const CHECK_FAILED_MESSAGE = 'We could not finish signing you in. Please reload the page.';
// The check waits on Frontegg behaviour its docs do not promise (the tenant list's loading flag, switchTenant
// calling back), so past this the app loads as it would without the check, with the failure message
const ACCOUNT_CHECK_DEADLINE_MS = 15_000;

type AccountCheckState = { phase: 'checking' } | { phase: 'switching' } | { phase: 'open'; failed: boolean };

// One check per page load, so its state sits in the app's jotai store, not in the hook: switchTenant
// unmounts everything under AuthWrapperFrontEgg while it runs, and the remounted hook must not check again.
const accountCheckState = atom<AccountCheckState>({ phase: 'checking' });

/** The token's account's place in the Frontegg hierarchy: the accounts beneath
 *  it, and the ancestors above it that the user belongs to, closest first. */
interface TokenTenantHierarchy {
  subTenantIds: Set<string>;
  memberParentIds: string[];
}

function buildTenantUrl(subdomain: string): string {
  const hostname = window.location.hostname;
  const baseDomain = hostname === 'localhost' ? 'foresightdata.com' : hostname.split('.').slice(1).join('.');
  return `${window.location.protocol}//${subdomain}.${baseDomain}`;
}

function removeSwitchedParams(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(ACCOUNT_SWITCHED_PARAM) && !params.has(ACCOUNT_SWITCHED_FROM_PARAM)) {
    return;
  }
  params.delete(ACCOUNT_SWITCHED_PARAM);
  params.delete(ACCOUNT_SWITCHED_FROM_PARAM);
  const search = params.toString();
  window.history.replaceState(
    null,
    '',
    window.location.pathname + (search ? `?${search}` : '') + window.location.hash
  );
}

export function useAccountSwitcher(): UseAccountSwitcherReturn {
  const { user } = useAuth();
  const tenantsState = useTenantsState();
  const { activeTenant, loading, switchingTenant, error: tenantsError } = tenantsState;
  const tenants = tenantListOrEmpty(tenantsState.tenants);
  const { switchTenant } = useTenantsActions();
  const { pushErrorToast } = useToastMessageState();
  const customerTenantId = useAtomValue(customerTenantIdState);
  const store = useStore();
  const checkState = useAtomValue(accountCheckState);
  const [error, setError] = useState<string | undefined>();

  const userTenantIds = user?.tenantIds;
  const hasMultipleMemberships = (userTenantIds?.length ?? 0) > 1;
  const accessToken = user?.accessToken;
  // The account the token was issued for, which Maggie checks permissions against.
  // Frontegg's activeTenant follows the user's active account, which can differ.
  const tokenTenantId = accessToken
    ? (decodeJwtPayload(accessToken)?.tenantId as string | undefined)
    : undefined;

  // The tenant list is loaded by useCustomerTenantSync, which the account gate
  // mounts above the app on every authenticated route

  // Parse tenants and extract subdomain from metadata
  const tenantsWithSubdomain: TenantWithSubdomain[] = useMemo(
    () =>
      tenants.map((tenant) => ({
        ...tenant,
        subdomain: parseMetadata(tenant.metadata)?.subdomain,
      })),
    [tenants]
  );

  // Only tenants with a subdomain are switchable; internal tenants (e.g. Starbreaker)
  // have no subdomain metadata and must not appear in or trigger the switcher
  const switchableTenants: TenantWithSubdomain[] = useMemo(
    () => tenantsWithSubdomain.filter((tenant) => tenant.subdomain),
    [tenantsWithSubdomain]
  );

  const hasMultipleTenants = switchableTenants.length > 1;

  // A parent account stays active on its sub-accounts' subdomains, so the
  // customer tenant, not the active account, is the one this session is on
  const currentTenant = useMemo(
    () => tenants.find((tenant) => tenant.tenantId === customerTenantId) ?? activeTenant,
    [tenants, customerTenantId, activeTenant]
  );

  // Frontegg's tenantsState.loading starts as true and only resolves once loadTenants()
  // runs, which the effect above triggers solely for users with multiple memberships —
  // so the raw membership count must gate this to avoid a permanent loading state for
  // single-tenant users
  const isInitialTenantLoad = loading && hasMultipleMemberships;

  // Only a user with several memberships can belong to a parent account, so
  // everyone else skips the lookups. The query is shared by every instance of
  // this hook (the account gate and the account menu), and a failed lookup resolves
  // to an empty hierarchy, which leaves the plain subdomain rule below in charge.
  const { data: hierarchyData } = useQuery({
    queryKey: ['frontegg-hierarchy', tokenTenantId],
    enabled: hasMultipleMemberships && !!tokenTenantId && !!accessToken,
    staleTime: HIERARCHY_STALE_TIME_MS,
    retry: false,
    queryFn: async () => {
      try {
        const [subTenants, parents] = await Promise.all([
          getActiveTenantSubTenants(accessToken ?? ''),
          getActiveTenantParents(accessToken ?? ''),
        ]);
        return { subTenantIds: subTenants.map((tenant) => tenant.tenantId), parents };
      } catch {
        return { subTenantIds: [], parents: [] };
      }
    },
  });

  const hierarchy = useMemo<TokenTenantHierarchy | null>(() => {
    if (!hierarchyData) {
      return null;
    }
    const memberships = new Set(userTenantIds);
    return {
      subTenantIds: new Set(hierarchyData.subTenantIds),
      memberParentIds: [...hierarchyData.parents]
        .sort((a, b) => a.level - b.level)
        .filter((parent) => memberships.has(parent.tenantId))
        .map((parent) => parent.tenantId),
    };
  }, [hierarchyData, userTenantIds]);

  // Until the token's account hierarchy is known the switcher must neither
  // auto-switch nor let a parent user switch by hand
  const hierarchyPending = hasMultipleMemberships && !!tokenTenantId && hierarchy === null;

  // Line the token's account up with the subdomain on load
  useEffect(() => {
    // An empty list waits whatever Frontegg's loading flag says: it reads false until loadTenants starts
    if (tenants.length === 0 || switchingTenant) {
      return;
    }
    // Read from the store, not the render, so the check runs at most once per page
    if (hierarchyPending || store.get(accountCheckState).phase !== 'checking') {
      return;
    }
    const finishCheck = (failed = false) => store.set(accountCheckState, { phase: 'open', failed });

    const currentSubdomain = getCurrentSubdomain();
    const tokenSubdomain = tenantsWithSubdomain.find(
      (tenant) => tenant.tenantId === tokenTenantId
    )?.subdomain;
    const matchingTenant = tenantsWithSubdomain.find((tenant) => tenant.subdomain === currentSubdomain);
    // The sign-in's list is enough to switch, but only the list loadTenants loads, which Frontegg documents,
    // can show that the subdomain's account is missing
    if (!matchingTenant && loading) {
      return;
    }

    // A parent account stays active on its sub-accounts' subdomains: its token
    // carries the roles held on the parent, and the customer API accepts it.
    if (matchingTenant && hierarchy?.subTenantIds.has(matchingTenant.tenantId)) {
      removeSwitchedParams();
      finishCheck();
      return;
    }

    // On the subdomain's own account, hand over to a parent the user belongs
    // to; anywhere else, move to the subdomain's account.
    const onSubdomainAccount = tokenSubdomain === currentSubdomain;
    const targetId = onSubdomainAccount ? hierarchy?.memberParentIds[0] : matchingTenant?.tenantId;
    const target =
      targetId && targetId !== tokenTenantId
        ? tenants.find((tenant) => tenant.tenantId === targetId)
        : undefined;
    if (!target) {
      if (onSubdomainAccount) {
        removeSwitchedParams();
      }
      finishCheck();
      return;
    }

    // One auto-switch per navigation, except a hop up from the subdomain's account
    // the last switch landed on (the hierarchy is only known for the token's account),
    // unless that hop goes back to where the switch came from. The same target
    // still pending means the switch didn't take.
    const searchParams = new URLSearchParams(window.location.search);
    const switchedParam = searchParams.get(ACCOUNT_SWITCHED_PARAM);
    if (switchedParam !== null) {
      removeSwitchedParams();
      const isHopUpAfterSwitch =
        onSubdomainAccount &&
        switchedParam === tokenTenantId &&
        searchParams.get(ACCOUNT_SWITCHED_FROM_PARAM) !== target.tenantId;
      if (!isHopUpAfterSwitch) {
        finishCheck(switchedParam === target.tenantId);
        return;
      }
    }

    store.set(accountCheckState, { phase: 'switching' });

    // Frontegg's own loader covers the app until this request returns, which the deadline cannot cut short
    switchTenant({
      tenantId: target.tenantId,
      callback: (isSuccess) => {
        if (!isSuccess) {
          finishCheck(true);
        } else {
          // Reload with the switched params in the URL so we can detect a failed switch on next load
          const reloadUrl = new URL(window.location.href);
          reloadUrl.searchParams.set(ACCOUNT_SWITCHED_PARAM, target.tenantId);
          if (tokenTenantId) {
            reloadUrl.searchParams.set(ACCOUNT_SWITCHED_FROM_PARAM, tokenTenantId);
          }
          // The cached boot reads belong to the account being left.
          void clearBootCache(300).finally(() => window.location.replace(reloadUrl.toString()));
        }
      },
    });
  }, [
    tenantsWithSubdomain,
    tenants,
    tokenTenantId,
    hierarchy,
    hierarchyPending,
    loading,
    switchingTenant,
    switchTenant,
    store,
  ]);

  const isCheckingAccount = hasMultipleMemberships && checkState.phase !== 'open';
  useEffect(() => {
    if (!isCheckingAccount) {
      return;
    }
    const deadline = setTimeout(() => {
      const { phase } = store.get(accountCheckState);
      if (phase !== 'open') {
        // Reported because a stall usually means an SDK upgrade changed Frontegg's undocumented behaviour;
        // the phase says whether the tenant list or the switch stalled
        captureMessage('Account check ran out of time', { level: 'warning', extra: { phase } });
        store.set(accountCheckState, { phase: 'open', failed: true });
      }
    }, ACCOUNT_CHECK_DEADLINE_MS);
    return () => clearTimeout(deadline);
  }, [isCheckingAccount, store]);

  // A failed switch may finish after Frontegg remounted this hook, so the toast follows the shared state
  const checkFailed = checkState.phase === 'open' && checkState.failed;
  useEffect(() => {
    if (checkFailed) {
      pushErrorToast({ message: CHECK_FAILED_MESSAGE });
      store.set(accountCheckState, { phase: 'open', failed: false });
    }
  }, [checkFailed, pushErrorToast, store]);

  const handleSwitchTenant = useCallback(
    (tenantId: string) => {
      setError(undefined);

      const targetTenant = tenantsWithSubdomain.find((t) => t.tenantId === tenantId);

      if (!targetTenant) {
        setError('Tenant not found');
        return;
      }

      if (!targetTenant.subdomain) {
        setError(`Subdomain not configured for tenant: ${targetTenant.name}`);
        return;
      }

      const url = buildTenantUrl(targetTenant.subdomain);

      // A parent account reaches its sub-accounts on their subdomains without
      // giving up the parent's token
      if (hierarchy !== null && hierarchy.subTenantIds.has(tenantId)) {
        navigateTo(url);
        return;
      }

      switchTenant({
        tenantId,
        callback: () => navigateTo(url),
      });
    },
    [tenantsWithSubdomain, hierarchy, switchTenant]
  );

  return {
    tenants: switchableTenants,
    currentTenant,
    isLoading: loading || hierarchyPending,
    isSwitching: switchingTenant,
    error: error || (tenantsError ? 'Failed to load tenants' : undefined),
    hasMultipleTenants,
    isInitialTenantLoad,
    isCheckingAccount,
    handleSwitchTenant,
  };
}
